import { flushSync } from "react-dom";
import { ColorScheme, CROSS_FADE_IGNORED_EVENTS } from "./segmented-control.constants";

/**
 * Checks whether a value is one of the colour schemes a user can choose.
 * @param value The value to check.
 * @returns Whether the value is a colour scheme.
 */
export const isColorScheme = (value: string): value is ColorScheme =>
    Object.values<string>(ColorScheme).includes(value);

/**
 * Stops an event before it reaches the page.
 * @param event The event.
 */
const ignoreEvent = (event: Event) => event.stopImmediatePropagation();

// How many cross-fades are running. A new choice can start one before the last has finished (quick arrow-key presses),
// and the events stay ignored until the last of them ends.
let runningCrossFades = 0;

/** Ends one cross-fade, and lets the ignored events through again once none are left running. */
const endCrossFade = () => {
    runningCrossFades -= 1;
    if (runningCrossFades === 0) {
        CROSS_FADE_IGNORED_EVENTS.forEach((type) => window.removeEventListener(type, ignoreEvent, true));
    }
};

/**
 * Applies a colour scheme with a short cross-fade between the old and new themes. Browsers without view transitions,
 * and users who ask their system for reduced motion, get the instant switch.
 * @param apply Applies the new colour scheme.
 */
export const withThemeCrossFade = (apply: () => void) => {
    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!("startViewTransition" in document) || prefersReducedMotion) {
        apply();
        return;
    }
    // Without this, a hover menu holding the picker would close on every theme change.
    if (runningCrossFades === 0) {
        CROSS_FADE_IGNORED_EVENTS.forEach((type) => window.addEventListener(type, ignoreEvent, true));
    }
    runningCrossFades += 1;
    // The update has to land inside the callback, so React renders the new selection before the new snapshot is taken.
    const transition = document.startViewTransition(() => flushSync(apply));
    // A fade can also end by failing (the update threw) or by being skipped for a newer one; either way it has ended.
    transition.finished.then(endCrossFade, endCrossFade);
    transition.ready.catch(() => undefined);
};
